import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Tooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { MAPBOX_TILE_URL, MAP_TILE_FALLBACK_URL } from '../utils/api';


// Fix for default marker icons in Leaflet when used with Webpack/Create React App
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41]
});
L.Marker.prototype.options.icon = DefaultIcon;

const getFallbackCenter = () => [52.370216, 4.895168]; // Amsterdam

const formatCoordinate = (value) => {
  if (value === null || value === undefined || value === '') return 'Unknown';

  const numberValue = Number(value);
  if (Number.isNaN(numberValue)) return String(value);

  return numberValue.toFixed(6);
};

const MiniHotelMap = ({ hotel }) => {
  const [mounted, setMounted] = useState(false);
  const mapRef = useRef(null);


  useEffect(() => {
    setMounted(true);
  }, []);

  const coords = useMemo(() => {
    const lat = parseFloat(hotel?.DHTHOT_latitude);
    const lng = parseFloat(hotel?.DHTHOT_longitude);

    if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
    return [lat, lng];
  }, [hotel]);

  useEffect(() => {
    if (!mapRef.current || !coords) return;
    const map = mapRef.current;

    // React-Leaflet only applies MapContainer's center on first mount.
    map.setView(coords, map.getZoom?.() || 12, { animate: false });
    map.invalidateSize?.();
  }, [coords]);

  if (!mounted) return null;

  return (
    <div className="mini-hotel-map-wrap">
      {coords ? (
        <MapContainer
          ref={mapRef}
          center={coords}
          zoom={12}
          scrollWheelZoom={false}
          dragging={false}
          style={{ height: '100%', width: '100%' }}
        >
          {MAPBOX_TILE_URL ? (
            <TileLayer
              attribution='&copy; <a href="https://www.mapbox.com/about/maps/">Mapbox</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url={MAPBOX_TILE_URL}
              tileSize={512}
              zoomOffset={-1}
            />
          ) : MAP_TILE_FALLBACK_URL ? (
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url={MAP_TILE_FALLBACK_URL}
            />
          ) : null}

          <Marker position={coords}>
            <Tooltip direction="top" offset={[0, -12]} opacity={1} className="hotel-map-tooltip">
              <div className="hotel-map-tooltip-content">
                <strong className="hotel-map-tooltip-title">{hotel?.DHTHOT_name || 'Hotel'}</strong>
                <span className="hotel-map-tooltip-address">
                  Lat: {formatCoordinate(hotel?.DHTHOT_latitude)}
                </span>
                <span className="hotel-map-tooltip-address">
                  Long: {formatCoordinate(hotel?.DHTHOT_longitude)}
                </span>
              </div>
            </Tooltip>
          </Marker>
        </MapContainer>
      ) : (
        <div className="mini-hotel-map-empty">No coordinates available</div>
      )}

      {!coords && (
        <div style={{ display: 'none' }}>
          {/* Keep leaflet happy in SSR-ish / mount timing cases */}
          {getFallbackCenter()}
        </div>
      )}
    </div>
  );
};

export default MiniHotelMap;
